import { useCallback, useContext, useEffect } from "react";
import moment from "moment";
import api from "../../services/api";
import UserPermission from "../../components/UserPermission";
import { SatisfactionMapContext } from "../../contexts/SatisfactionMapFilterContext";

export function useSatisfactionMapFilter() {
  const userPermission = UserPermission();

  const {
    setComplaints,
    setLoading,
    setSelectedItem,
    setMapPosition,
    setMapZoom,
    rangeOccurrences,
    evaluation,
    neighborhood,
    area,
    group,
    stateOccurrence,
    status,
    complaintTarget,
    late,
    ...props
  } = useContext(SatisfactionMapContext);

  const updateOccurrences = useCallback(async () => {
    setLoading(true);
    try {
      const { data: evaluations } = await api.get(`${userPermission}/evaluations`);
      const filteredEvaluations = evaluations.filter((ev) => {
        const date = moment(ev.createdAt);
        const inRange =
          date.isSameOrAfter(moment(rangeOccurrences.startDate)) &&
          date.isSameOrBefore(moment(rangeOccurrences.endDate).endOf("day"));

        const matchReview =
          evaluation === "Todos" || Number(ev.review) === Number(evaluation);

        return inRange && matchReview && ev.review && ev.complaintId;
      });

      const complaintIds = [...new Set(filteredEvaluations.map(ev => ev.complaintId))];

      const complaintsWithEval = await Promise.all(
        complaintIds.map(async (id) => {
          try {
            const { data: complaint } = await api.get(`${userPermission}/complaints/${id}`);

            if (complaint && complaint.latitude && (complaint.longitute || complaint.longitude)) {
              const relatedEvaluations = filteredEvaluations.filter(ev => ev.complaintId === id);
              const latestEval = relatedEvaluations[relatedEvaluations.length - 1];

              if (latestEval?.review) {
                return {
                  ...complaint,
                  latitude: parseFloat(complaint.latitude),
                  longitute: parseFloat(complaint.longitute ?? complaint.longitude),
                  evaluation: {
                    review: latestEval.review,
                    description: latestEval.description || "",
                    like: latestEval.like || null,
                    createdAt: latestEval.createdAt || null,
                  },
                };
              }
            }

            return null;
          } catch (err) {
            console.warn(`⚠️ Erro ao buscar complaint ${id}:`, err);
            return null;
          }
        })
      );
      
      const validComplaints = complaintsWithEval.filter(Boolean);
      const filteredComplaints = validComplaints.filter(c => {

        const matchNeighborhood = 
          neighborhood.name === "Todos" || c.neighborhood === neighborhood.name;
        const matchArea = 
          area === "Todos" || c.area === area;
        const matchGroup = 
          group === "Todos" || c.group === group;
        const matchStatus = 
          status === "Todos" || c.status === status;

        const matchTarget = 
          complaintTarget === "Todos" || c.complaintTarget === complaintTarget;
        const isForwarded = c.group !== null && c.group !== undefined && c.group !== "";
        const matchStateOccurrence = 
          stateOccurrence === "Todos" || 
          (stateOccurrence === 'true' ? isForwarded : !isForwarded);
        const isResolved = c.status === "Resolvido" || c.status === "Concluído";
        const isLateCalculated = !isResolved && moment(c.datePrevision).isBefore(moment());
        const matchLate = 
          late === "Todos" || 
          (late === 'true' ? isLateCalculated : !isLateCalculated);

        return matchNeighborhood && 
               matchArea && 
               matchGroup && 
               matchStatus && 
               matchStateOccurrence && 
               matchTarget &&
               matchLate;
      });

      setComplaints(filteredComplaints);

    } catch (error) {
      console.error("Erro ao processar filtros:", error);
      setComplaints([]);
    } finally {
      setLoading(false);
      setSelectedItem(null);
    }
  }, [
    userPermission, 
    setComplaints, 
    setLoading, 
    setSelectedItem, 
    rangeOccurrences, 
    evaluation,
    neighborhood,
    area,
    group,
    stateOccurrence,
    status,
    complaintTarget,
    late
  ]);

  useEffect(() => {
    updateOccurrences();
  }, [updateOccurrences]);

  return {
    setComplaints,
    rangeOccurrences,
    evaluation,
    setLoading,
    setSelectedItem,
    setMapPosition,
    setMapZoom,
    neighborhood,
    area,
    group,
    stateOccurrence,
    status,
    complaintTarget,
    late,
    ...props,
  };
}